import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, GraduationCap } from "lucide-react";
import { Reveal, Counter, SectionHeading, Overline } from "./primitives";
import { STATS, LIFECYCLE, IMAGES } from "@/data/site";
import { useModal } from "./Forms";

const MARQUEE_WORDS = [
  "BRAND", "TECHNOLOGY", "MARKETING", "ADMISSIONS", "STUDENT ACQUISITION",
  "CALLING", "COUNSELLING", "GROWTH", "ANALYTICS",
];

export function Marquee() {
  const items = [...MARQUEE_WORDS, ...MARQUEE_WORDS];
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-ink-1 py-6" data-testid="marquee">
      <div className="flex w-max animate-marquee-slow items-center gap-10 whitespace-nowrap">
        {items.map((w, i) => (
          <span key={i} className="flex items-center gap-10 font-display text-2xl font-medium tracking-tight text-white/25 md:text-3xl">
            {w} <span className="text-brand-cyan">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function StatsStrip() {
  return (
    <section className="relative bg-ink-0 py-20" data-testid="stats-strip">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-3 lg:grid-cols-5">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="border-l border-white/10 pl-5">
                <div className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl">
                  {s.text ? (
                    <span className="text-gradient">{s.text}</span>
                  ) : (
                    <Counter to={s.value} suffix={s.suffix} />
                  )}
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-white/45">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Positioning() {
  const { openInstitution, openStudent } = useModal();
  return (
    <section id="about" className="relative overflow-hidden bg-ink-1 py-28" data-testid="positioning">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Overline>01 — The Difference</Overline>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
              YOUR INSTITUTION DESERVES MORE THAN <span className="text-gradient">AN AGENCY.</span>
            </h2>
            <p className="mt-6 font-display text-xl text-white/80 md:text-2xl">It needs a growth partner.</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/55">
              A3 Global Marketing &amp; Education works as an extended growth and technology team for educational institutions.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/55">
              From building your digital infrastructure to attracting students, managing enquiries, powering calling operations and supporting the admission journey, we bring multiple capabilities under one roof.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-6">
            <PositionCard
              id="institutions"
              icon={Building2}
              tag="For Institutions"
              headline="Build. Market. Acquire. Convert. Grow."
              cta="Explore Institution Solutions"
              onClick={openInstitution}
              img={IMAGES.campus}
              accent
            />
            <PositionCard
              id="students"
              icon={GraduationCap}
              tag="For Students"
              headline="Discover. Compare. Choose. Apply."
              cta="Find Your Dream College"
              onClick={openStudent}
              img={IMAGES.students}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function PositionCard({ id, icon: Icon, tag, headline, cta, onClick, accent, img }) {
  return (
    <Reveal className="h-full">
      <motion.div
        id={id}
        whileHover={{ y: -6 }}
        className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl border transition-colors duration-300 ${
          accent ? "border-brand-cyan/30 bg-gradient-to-br from-brand-blue/20 to-ink-2" : "border-white/10 bg-ink-2 hover:border-white/25"
        }`}
      >
        <div className="relative h-36 overflow-hidden">
          <img src={img} alt={tag} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-2 via-ink-2/50 to-transparent" />
        </div>
        <div className="flex flex-1 flex-col justify-between p-7 pt-4">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-cyan/10 blur-3xl transition-opacity duration-500 group-hover:opacity-80" />
        <div>
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-cyan/15 text-brand-cyan">
            <Icon className="h-6 w-6" />
          </div>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-widest text-brand-cyan">{tag}</p>
          <h3 className="mt-3 font-display text-2xl font-semibold leading-tight text-white">{headline}</h3>
        </div>
        <button onClick={onClick} className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-white transition-colors hover:text-brand-cyan" data-testid={`position-cta-${id}`}>
          {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        </div>
      </motion.div>
    </Reveal>
  );
}

export function GrowthLifecycle() {
  return (
    <section className="relative overflow-hidden bg-ink-0 py-28" data-testid="growth-lifecycle">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="02 — The Growth Engine"
          title={<>WE RUN THE <span className="text-gradient">ENTIRE GROWTH ENGINE.</span></>}
          subtitle="From campus branding to student admission — we connect the entire journey."
        />

        <div className="relative mt-16">
          {/* connecting line */}
          <div className="absolute left-0 top-8 hidden h-px w-full bg-gradient-to-r from-transparent via-brand-cyan/40 to-transparent lg:block" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {LIFECYCLE.map((stage, i) => (
              <Reveal key={stage.n} delay={(i % 4) * 0.08}>
                <div className="group relative rounded-2xl border border-white/10 bg-ink-1 p-6 transition-colors duration-300 hover:border-brand-cyan/40">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 text-brand-cyan transition-colors group-hover:bg-brand-cyan/15">
                      <stage.icon className="h-5 w-5" />
                    </div>
                    <span className="font-display text-3xl font-bold text-white/10 transition-colors group-hover:text-brand-cyan/30">{stage.n}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">{stage.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/50">{stage.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
